import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useMobileContext } from '../components/Layout';
import { MobileHeader } from '../components/mobile';
import { 
  AlertTriangle, Plus, Search, Filter, Eye, FileText, Building,
  Calendar, Clock, Euro, Send, Download, X, CheckCircle, XCircle,
  Zap, Droplet, Key, Thermometer, Home, Flame, ShieldAlert, CloudLightning,
  Upload, MessageSquare, History, User, Mail, Phone, ArrowUpDown, Trees
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping for categories
const categoryIcons = {
  plomberie: Droplet,
  electricite: Zap,
  ascenseur: ArrowUpDown,
  chauffage: Thermometer,
  serrurerie: Key,
  toiture: Home,
  facade: Building,
  espaces_verts: Trees,
  degat_eaux: Droplet,
  incendie: Flame,
  vol_vandalisme: ShieldAlert,
  catastrophe_naturelle: CloudLightning,
  autre: AlertTriangle
};

const severityColors = {
  1: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
  2: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200' },
  3: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' }
};

const urgencyColors = {
  normal: { bg: 'bg-gray-100', text: 'text-gray-800' },
  urgent: { bg: 'bg-orange-100', text: 'text-orange-800' },
  tres_urgent: { bg: 'bg-red-100', text: 'text-red-800' }
};

const statusColors = {
  nouveau: 'bg-blue-100 text-blue-800',
  os_genere: 'bg-purple-100 text-purple-800',
  os_envoye: 'bg-indigo-100 text-indigo-800',
  en_cours: 'bg-yellow-100 text-yellow-800',
  expertise: 'bg-orange-100 text-orange-800',
  resolu: 'bg-green-100 text-green-800',
  cloture: 'bg-gray-100 text-gray-800'
};

const IncidentsSinistres = () => {
  const { user, token } = useAuth();
  const { isMobile, onMenuClick } = useMobileContext();
  
  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Config data
  const [categories, setCategories] = useState([]);
  const [severityLevels, setSeverityLevels] = useState({});
  const [urgencyLevels, setUrgencyLevels] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [prestataires, setPrestataires] = useState([]);
  const [assureurs, setAssureurs] = useState([]);
  
  // Filters
  const [severityFilter, setSeverityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [buildingFilter, setBuildingFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showOSModal, setShowOSModal] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    building_id: '',
    title: '',
    description: '',
    severity_level: 1,
    category: 'autre',
    urgency: 'normal',
    location: '',
    lot_concerne: '',
    estimated_amount: '',
    amount_limit: '',
    special_instructions: '',
    prestataire_id: '',
    assureur_id: '',
    photos: []
  });
  
  // OS form data
  const [osFormData, setOsFormData] = useState({
    custom_instructions: '',
    intervention_date: '',
    contact_sur_place: '',
    send_to_prestataire: true,
    send_to_assureur: false,
    additional_emails: ''
  });
  
  const fileInputRef = useRef(null);

  const fetchConfig = useCallback(async () => {
    try {
      const [catRes, sevRes, urgRes, statusRes, buildRes, prestRes] = await Promise.all([
        axios.get(`${API}/incidents-sinistres/config/categories`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/incidents-sinistres/config/severity-levels`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/incidents-sinistres/config/urgency-levels`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/incidents-sinistres/config/statuses`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/immeubles`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/incidents-sinistres/prestataires/list`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setCategories(catRes.data.categories || []);
      setSeverityLevels(sevRes.data.severity_levels || {});
      setUrgencyLevels(urgRes.data.urgency_levels || []);
      setStatuses(statusRes.data.statuses || []);
      setBuildings(buildRes.data || []);
      setPrestataires(prestRes.data.prestataires || []);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  }, [token]);

  const fetchIncidents = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (severityFilter) params.append('severity', severityFilter);
      if (categoryFilter) params.append('category', categoryFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (buildingFilter) params.append('building_id', buildingFilter);
      
      const [incidentsRes, statsRes] = await Promise.all([
        axios.get(`${API}/incidents-sinistres?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/incidents-sinistres/stats${buildingFilter ? `?building_id=${buildingFilter}` : ''}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setIncidents(incidentsRes.data.incidents || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching incidents:', error);
    }
  }, [token, severityFilter, categoryFilter, statusFilter, buildingFilter]);

  const fetchAssureurs = useCallback(async (buildingId) => {
    if (!buildingId) return;
    try {
      const res = await axios.get(`${API}/incidents-sinistres/assureurs/list?building_id=${buildingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssureurs(res.data.assureurs || []);
    } catch (error) {
      console.error('Error fetching assureurs:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchConfig();
      await fetchIncidents();
      setLoading(false);
    };
    loadData();
  }, [fetchConfig, fetchIncidents]);

  useEffect(() => {
    if (formData.building_id && formData.severity_level === 3) {
      fetchAssureurs(formData.building_id);
    }
  }, [formData.building_id, formData.severity_level, fetchAssureurs]);

  const handleCreateIncident = async (e) => {
    e.preventDefault();
    
    if (!formData.building_id || !formData.title) {
      setMessage({ type: 'error', text: 'Immeuble et titre requis' });
      return;
    }
    
    try {
      const payload = {
        ...formData,
        severity_level: parseInt(formData.severity_level),
        estimated_amount: formData.estimated_amount ? parseFloat(formData.estimated_amount) : null,
        amount_limit: formData.amount_limit ? parseFloat(formData.amount_limit) : null
      };
      
      const res = await axios.post(`${API}/incidents-sinistres`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage({ type: 'success', text: `Incident ${res.data.reference} créé` });
      setShowCreateModal(false);
      resetForm();
      fetchIncidents();
    } catch (error) {
      console.error('Error creating incident:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de la création' });
    }
  };

  const resetForm = () => {
    setFormData({
      building_id: '',
      title: '',
      description: '',
      severity_level: 1,
      category: 'autre',
      urgency: 'normal',
      location: '',
      lot_concerne: '',
      estimated_amount: '',
      amount_limit: '',
      special_instructions: '',
      prestataire_id: '',
      assureur_id: '',
      photos: []
    });
  };

  const handleViewDetail = async (incident) => {
    try {
      const res = await axios.get(`${API}/incidents-sinistres/${incident.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedIncident(res.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error fetching incident detail:', error);
    }
  };

  const handleGenerateOS = async () => {
    if (!selectedIncident) return;
    
    try {
      const res = await axios.post(`${API}/incidents-sinistres/${selectedIncident.id}/generate-os`, {
        custom_instructions: osFormData.custom_instructions,
        intervention_date: osFormData.intervention_date,
        contact_sur_place: osFormData.contact_sur_place
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage({ type: 'success', text: `OS ${res.data.os_number} généré` });
      // Refresh incident detail
      handleViewDetail(selectedIncident);
    } catch (error) {
      console.error('Error generating OS:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de la génération' });
    }
  };

  const handleSendOS = async () => {
    if (!selectedIncident) return;
    
    const additionalEmails = osFormData.additional_emails
      .split(',')
      .map(e => e.trim())
      .filter(e => e);
    
    try {
      const res = await axios.post(`${API}/incidents-sinistres/${selectedIncident.id}/send-os`, {
        send_to_prestataire: osFormData.send_to_prestataire,
        send_to_assureur: osFormData.send_to_assureur && selectedIncident.severity_level === 3,
        recipients: additionalEmails
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage({ type: 'success', text: `OS envoyé à ${res.data.recipients.join(', ')}` });
      setShowOSModal(false);
      handleViewDetail(selectedIncident);
    } catch (error) {
      console.error('Error sending OS:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de l\'envoi' });
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'Fichier trop volumineux (max 5 Mo)' });
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          photos: [...prev.photos, {
            name: file.name,
            type: file.type,
            data: reader.result
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const getCategoryIcon = (categoryId) => {
    const Icon = categoryIcons[categoryId] || AlertTriangle;
    return <Icon size={18} />;
  };

  const getSeverityBadge = (level) => {
    const colors = severityColors[level] || severityColors[1];
    const labels = { 1: 'Mineur', 2: 'Moyen', 3: 'Majeur / Sinistre' };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text} border ${colors.border}`}>
        {labels[level] || 'Niveau ' + level}
      </span>
    );
  };

  const filteredIncidents = incidents.filter(inc => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return inc.title?.toLowerCase().includes(q) || inc.reference?.toLowerCase().includes(q);
  });

  if (loading) {
    return (
      <>
        {isMobile && <MobileHeader title="Incidents & Sinistres" onMenuClick={onMenuClick} />}
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00D3A8]"></div>
        </div>
      </>
    );
  }

  return (
    <>
      {isMobile && <MobileHeader title="Incidents & Sinistres" onMenuClick={onMenuClick} />}
      <div className={`space-y-4 md:space-y-6 ${isMobile ? 'p-4' : ''}`}>
        {/* Header */}
        {!isMobile && (
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-800 flex items-center">
              <AlertTriangle className="mr-3 text-[#00D3A8]" size={28} />
              Incidents & Sinistres
            </h1>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-[#00D3A8] hover:bg-[#00C7A1] text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              data-testid="create-incident-btn"
            >
              <Plus size={20} />
              <span>Nouvel Incident</span>
            </button>
          </div>
        )}

        {/* Message */}
        {message.text && (
          <div className={`p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
            'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {message.text}
            <button onClick={() => setMessage({ type: '', text: '' })} className="float-right">
              <X size={16} />
            </button>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-400">
            <p className="text-sm text-gray-500">Total</p>
            <p className="text-2xl font-bold">{stats.total || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-400">
            <p className="text-sm text-gray-500">Mineurs</p>
            <p className="text-2xl font-bold">{stats.by_severity?.[1] || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-yellow-400">
            <p className="text-sm text-gray-500">Moyens</p>
            <p className="text-2xl font-bold">{stats.by_severity?.[2] || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-400">
            <p className="text-sm text-gray-500">Majeurs</p>
            <p className="text-2xl font-bold">{stats.by_severity?.[3] || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-400">
            <p className="text-sm text-gray-500">OS en attente</p>
            <p className="text-2xl font-bold">{stats.pending_os || 0}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
              />
            </div>
            
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
            >
              <option value="">Tous niveaux</option>
              <option value="1">Mineur</option>
              <option value="2">Moyen</option>
              <option value="3">Majeur / Sinistre</option>
            </select>
            
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
            >
              <option value="">Toutes catégories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
            >
              <option value="">Tous statuts</option>
              {statuses.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            
            <select
              value={buildingFilter}
              onChange={(e) => setBuildingFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
            >
              <option value="">Tous immeubles</option>
              {buildings.map(b => (
                <option key={b.id} value={b.id}>{b.nom}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Mobile Create Button */}
        {isMobile && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="w-full bg-[#00D3A8] hover:bg-[#00C7A1] text-white px-4 py-3 rounded-lg flex items-center justify-center space-x-2"
          >
            <Plus size={20} />
            <span>Nouvel Incident</span>
          </button>
        )}

        {/* Incidents List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {filteredIncidents.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <AlertTriangle size={48} className="mx-auto mb-4 opacity-50" />
              <p>Aucun incident trouvé</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredIncidents.map(incident => (
                <div key={incident.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 flex-wrap gap-2">
                        <span className="text-gray-400">{getCategoryIcon(incident.category)}</span>
                        <span className="text-sm text-gray-500 font-mono">{incident.reference}</span>
                        <h3 className="font-semibold text-gray-800">{incident.title}</h3>
                        {getSeverityBadge(incident.severity_level)}
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[incident.status] || 'bg-gray-100'}`}>
                          {statuses.find(s => s.id === incident.status)?.name || incident.status}
                        </span>
                      </div>
                      <div className="mt-2 text-sm text-gray-500 space-y-1">
                        <p className="flex items-center">
                          <Building size={14} className="mr-2" />
                          {incident.building_name || 'Immeuble non spécifié'}
                        </p>
                        {incident.prestataire_name && (
                          <p className="flex items-center">
                            <User size={14} className="mr-2" />
                            Prestataire: {incident.prestataire_name}
                          </p>
                        )}
                        <p className="flex items-center">
                          <Calendar size={14} className="mr-2" />
                          {new Date(incident.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex flex-col space-y-2 ml-4">
                      <button
                        onClick={() => handleViewDetail(incident)}
                        className="text-gray-500 hover:text-[#00D3A8] p-2 rounded-lg hover:bg-gray-100"
                        title="Voir détails"
                        data-testid={`view-incident-${incident.id}`}
                      >
                        <Eye size={18} />
                      </button>
                      {!incident.os_generated && (
                        <button
                          onClick={() => { setSelectedIncident(incident); setShowOSModal(true); }}
                          className="text-purple-500 hover:text-purple-700 p-2 rounded-lg hover:bg-purple-50"
                          title="Générer OS"
                        >
                          <FileText size={18} />
                        </button>
                      )}
                      {incident.os_generated && !incident.os_sent && (
                        <button
                          onClick={() => { setSelectedIncident(incident); setShowOSModal(true); }}
                          className="text-indigo-500 hover:text-indigo-700 p-2 rounded-lg hover:bg-indigo-50"
                          title="Envoyer OS"
                        >
                          <Send size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Create Incident Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 bg-gray-50">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-gray-800">Nouvel Incident / Sinistre</h2>
                  <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="text-gray-400 hover:text-gray-600">
                    <X size={24} />
                  </button>
                </div>
              </div>
              
              <form onSubmit={handleCreateIncident} className="p-6 space-y-6">
                {/* Severity Level */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Niveau de gravité <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-3 gap-4">
                    {[1, 2, 3].map(level => (
                      <button
                        key={level}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, severity_level: level }))}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                          formData.severity_level === level
                            ? level === 1 ? 'border-green-500 bg-green-50' 
                              : level === 2 ? 'border-yellow-500 bg-yellow-50'
                              : 'border-red-500 bg-red-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="font-semibold">{severityLevels[level]?.name || `Niveau ${level}`}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {severityLevels[level]?.notify?.includes('assureur') ? 'Prestataire + Assureur' : 'Prestataire'}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Building & Category */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Immeuble <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.building_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, building_id: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                      required
                    >
                      <option value="">Sélectionner</option>
                      {buildings.map(b => (
                        <option key={b.id} value={b.id}>{b.nom}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                    >
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Titre <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                    placeholder="Ex: Fuite d'eau au 3ème étage"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                    rows={3}
                    placeholder="Décrivez l'incident en détail..."
                  />
                </div>

                {/* Location & Urgency */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Localisation précise</label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                      placeholder="Ex: Sous-sol, local poubelles"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Urgence</label>
                    <select
                      value={formData.urgency}
                      onChange={(e) => setFormData(prev => ({ ...prev, urgency: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                    >
                      {urgencyLevels.map(u => (
                        <option key={u.id} value={u.id}>{u.name} ({u.delay})</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Lot & Amount */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Lot concerné</label>
                    <input
                      type="text"
                      value={formData.lot_concerne}
                      onChange={(e) => setFormData(prev => ({ ...prev, lot_concerne: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                      placeholder="Ex: Lot 12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estimation (€)</label>
                    <input
                      type="number"
                      value={formData.estimated_amount}
                      onChange={(e) => setFormData(prev => ({ ...prev, estimated_amount: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Plafond (€)</label>
                    <input
                      type="number"
                      value={formData.amount_limit}
                      onChange={(e) => setFormData(prev => ({ ...prev, amount_limit: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Prestataire & Assureur */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prestataire</label>
                    <select
                      value={formData.prestataire_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, prestataire_id: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                    >
                      <option value="">Sélectionner</option>
                      {prestataires.map(p => (
                        <option key={p.id} value={p.id}>{p.societe || p.nom}</option>
                      ))}
                    </select>
                  </div>
                  {formData.severity_level === 3 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Assureur</label>
                      <select
                        value={formData.assureur_id}
                        onChange={(e) => setFormData(prev => ({ ...prev, assureur_id: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                      >
                        <option value="">Sélectionner</option>
                        {assureurs.map(a => (
                          <option key={a.id} value={a.id}>{a.compagnie} - {a.numero_contrat}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Special Instructions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Instructions particulières</label>
                  <textarea
                    value={formData.special_instructions}
                    onChange={(e) => setFormData(prev => ({ ...prev, special_instructions: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                    rows={2}
                    placeholder="Instructions spéciales pour l'intervention..."
                  />
                </div>

                {/* Photos */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Photos</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full flex items-center justify-center space-x-2 text-gray-500 hover:text-[#00D3A8]"
                    >
                      <Upload size={20} />
                      <span>Ajouter des photos</span>
                    </button>
                    {formData.photos.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {formData.photos.map((photo, idx) => (
                          <div key={idx} className="relative">
                            <img src={photo.data} alt="" className="w-16 h-16 object-cover rounded" />
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({
                                ...prev,
                                photos: prev.photos.filter((_, i) => i !== idx)
                              }))}
                              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Submit */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => { setShowCreateModal(false); resetForm(); }}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="bg-[#00D3A8] hover:bg-[#00C7A1] text-white px-6 py-2 rounded-lg flex items-center space-x-2"
                  >
                    <Plus size={20} />
                    <span>Créer l'incident</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Detail Modal */}
        {showDetailModal && selectedIncident && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="text-sm text-gray-500 font-mono">{selectedIncident.reference}</span>
                      {getSeverityBadge(selectedIncident.severity_level)}
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[selectedIncident.status]}`}>
                        {statuses.find(s => s.id === selectedIncident.status)?.name}
                      </span>
                    </div>
                    <h2 className="text-xl font-bold text-gray-800">{selectedIncident.title}</h2>
                  </div>
                  <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X size={24} />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Immeuble</p>
                    <p className="font-medium">{selectedIncident.building?.nom}</p>
                    <p className="text-sm text-gray-500">{selectedIncident.building?.adresse}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Catégorie</p>
                    <p className="font-medium flex items-center">
                      {getCategoryIcon(selectedIncident.category)}
                      <span className="ml-2">{categories.find(c => c.id === selectedIncident.category)?.name}</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Urgence</p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      urgencyColors[selectedIncident.urgency]?.bg || 'bg-gray-100'
                    } ${urgencyColors[selectedIncident.urgency]?.text || 'text-gray-800'}`}>
                      {urgencyLevels.find(u => u.id === selectedIncident.urgency)?.name}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Créé le</p>
                    <p className="font-medium">{new Date(selectedIncident.created_at).toLocaleString('fr-FR')}</p>
                  </div>
                </div>

                {/* Description */}
                {selectedIncident.description && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Description</p>
                    <p className="text-gray-700">{selectedIncident.description}</p>
                  </div>
                )}

                {/* Location */}
                {(selectedIncident.location || selectedIncident.lot_concerne) && (
                  <div className="grid grid-cols-2 gap-4">
                    {selectedIncident.location && (
                      <div>
                        <p className="text-sm text-gray-500">Localisation</p>
                        <p className="font-medium">{selectedIncident.location}</p>
                      </div>
                    )}
                    {selectedIncident.lot_concerne && (
                      <div>
                        <p className="text-sm text-gray-500">Lot concerné</p>
                        <p className="font-medium">{selectedIncident.lot_concerne}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Prestataire & Assureur */}
                {(selectedIncident.prestataire || selectedIncident.assureur) && (
                  <div className="grid grid-cols-2 gap-4">
                    {selectedIncident.prestataire && (
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-500 mb-1">Prestataire</p>
                        <p className="font-medium">{selectedIncident.prestataire.societe || selectedIncident.prestataire.nom}</p>
                        {selectedIncident.prestataire.email && (
                          <p className="text-sm text-gray-500 flex items-center mt-1">
                            <Mail size={12} className="mr-1" /> {selectedIncident.prestataire.email}
                          </p>
                        )}
                      </div>
                    )}
                    {selectedIncident.assureur && (
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-500 mb-1">Assureur</p>
                        <p className="font-medium">{selectedIncident.assureur.compagnie}</p>
                        <p className="text-sm text-gray-500">Contrat: {selectedIncident.assureur.numero_contrat}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* OS Status */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-medium text-purple-800 flex items-center mb-2">
                    <FileText size={18} className="mr-2" />
                    Ordre de Service
                  </h4>
                  {selectedIncident.os_generated ? (
                    <div className="space-y-2">
                      <p className="text-sm text-purple-700">
                        N° {selectedIncident.os_number} - Généré le {new Date(selectedIncident.os_generated_at).toLocaleString('fr-FR')}
                      </p>
                      {selectedIncident.os_sent ? (
                        <p className="text-sm text-green-700 flex items-center">
                          <CheckCircle size={14} className="mr-1" />
                          Envoyé le {new Date(selectedIncident.os_sent_at).toLocaleString('fr-FR')} à {selectedIncident.os_sent_to?.join(', ')}
                        </p>
                      ) : (
                        <button
                          onClick={() => setShowOSModal(true)}
                          className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 text-sm"
                        >
                          <Send size={16} />
                          <span>Envoyer l'OS</span>
                        </button>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowOSModal(true)}
                      className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 text-sm"
                    >
                      <FileText size={16} />
                      <span>Générer l'Ordre de Service</span>
                    </button>
                  )}
                </div>

                {/* History */}
                {selectedIncident.history?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-800 flex items-center mb-3">
                      <History size={18} className="mr-2" />
                      Historique
                    </h4>
                    <div className="space-y-2">
                      {selectedIncident.history.map((entry, idx) => (
                        <div key={idx} className="flex items-start space-x-3 text-sm">
                          <div className="w-2 h-2 rounded-full bg-gray-400 mt-1.5" />
                          <div>
                            <p className="text-gray-700">{entry.details}</p>
                            <p className="text-gray-400 text-xs">
                              {entry.user_name} - {new Date(entry.created_at).toLocaleString('fr-FR')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* OS Modal */}
        {showOSModal && selectedIncident && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
              <div className="p-6 border-b border-gray-200 bg-purple-50">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-purple-800 flex items-center">
                    <FileText size={24} className="mr-2" />
                    {selectedIncident.os_generated ? 'Envoyer l\'OS' : 'Générer l\'Ordre de Service'}
                  </h2>
                  <button onClick={() => setShowOSModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X size={24} />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                {!selectedIncident.os_generated ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date d'intervention souhaitée</label>
                      <input
                        type="date"
                        value={osFormData.intervention_date}
                        onChange={(e) => setOsFormData(prev => ({ ...prev, intervention_date: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Contact sur place</label>
                      <input
                        type="text"
                        value={osFormData.contact_sur_place}
                        onChange={(e) => setOsFormData(prev => ({ ...prev, contact_sur_place: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        placeholder="Nom et téléphone du contact"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Instructions complémentaires</label>
                      <textarea
                        value={osFormData.custom_instructions}
                        onChange={(e) => setOsFormData(prev => ({ ...prev, custom_instructions: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        rows={3}
                      />
                    </div>
                    <div className="flex justify-end space-x-3 pt-4">
                      <button onClick={() => setShowOSModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                        Annuler
                      </button>
                      <button
                        onClick={handleGenerateOS}
                        className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-2 rounded-lg flex items-center space-x-2"
                      >
                        <FileText size={18} />
                        <span>Générer l'OS</span>
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-green-800 text-sm">OS {selectedIncident.os_number} prêt à envoyer</p>
                    </div>
                    
                    <div className="space-y-3">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={osFormData.send_to_prestataire}
                          onChange={(e) => setOsFormData(prev => ({ ...prev, send_to_prestataire: e.target.checked }))}
                          className="rounded text-[#00D3A8]"
                        />
                        <span className="text-sm">Envoyer au prestataire</span>
                        {selectedIncident.prestataire?.email && (
                          <span className="text-xs text-gray-500">({selectedIncident.prestataire.email})</span>
                        )}
                      </label>
                      
                      {selectedIncident.severity_level === 3 && (
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={osFormData.send_to_assureur}
                            onChange={(e) => setOsFormData(prev => ({ ...prev, send_to_assureur: e.target.checked }))}
                            className="rounded text-[#00D3A8]"
                          />
                          <span className="text-sm">Envoyer à l'assureur</span>
                          {selectedIncident.assureur?.email && (
                            <span className="text-xs text-gray-500">({selectedIncident.assureur.email})</span>
                          )}
                        </label>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Autres destinataires (emails séparés par virgule)</label>
                      <input
                        type="text"
                        value={osFormData.additional_emails}
                        onChange={(e) => setOsFormData(prev => ({ ...prev, additional_emails: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        placeholder="email1@example.com, email2@example.com"
                      />
                    </div>
                    
                    <div className="flex justify-end space-x-3 pt-4">
                      <button onClick={() => setShowOSModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                        Annuler
                      </button>
                      <button
                        onClick={handleSendOS}
                        className="bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-2 rounded-lg flex items-center space-x-2"
                      >
                        <Send size={18} />
                        <span>Envoyer</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default IncidentsSinistres;
